import React, { useState, useEffect } from 'react';
import { Modal, Table, Badge, Button, Form, Row, Col } from 'react-bootstrap';
import { FaChartLine, FaDownload, FaCalendar, FaRupeeSign } from 'react-icons/fa';

const CommissionSummaryModal = ({ show, onHide }) => {
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    endDate: new Date().toISOString().split('T')[0]
  });

  const [commissionData, setCommissionData] = useState([
    {
      id: 1,
      date: '2025-01-30',
      service: 'Money Transfer',
      transactionAmount: 25000,
      commissionRate: 0.5,
      commissionEarned: 125,
      status: 'credited',
      transactionId: 'TXN123456789'
    },
    {
      id: 2,
      date: '2025-01-29',
      service: 'Mobile Recharge',
      transactionAmount: 500,
      commissionRate: 2.0,
      commissionEarned: 10,
      status: 'pending',
      transactionId: 'TXN123456788'
    },
    {
      id: 3,
      date: '2025-01-28',
      service: 'Money Transfer',
      transactionAmount: 15000,
      commissionRate: 0.5,
      commissionEarned: 75,
      status: 'credited',
      transactionId: 'TXN123456787'
    },
    {
      id: 4,
      date: '2025-01-27',
      service: 'Bill Payment',
      transactionAmount: 2500,
      commissionRate: 1.5,
      commissionEarned: 37.5,
      status: 'credited',
      transactionId: 'TXN123456786'
    }
  ]);

  const [summary, setSummary] = useState({
    totalEarned: 0,
    totalCredited: 0,
    totalPending: 0,
    totalTransactions: 0
  });

  useEffect(() => {
    // Calculate summary
    const totalEarned = commissionData.reduce((sum, item) => sum + item.commissionEarned, 0);
    const totalCredited = commissionData.filter(item => item.status === 'credited')
                                     .reduce((sum, item) => sum + item.commissionEarned, 0);
    const totalPending = commissionData.filter(item => item.status === 'pending')
                                      .reduce((sum, item) => sum + item.commissionEarned, 0);
    
    setSummary({
      totalEarned,
      totalCredited,
      totalPending,
      totalTransactions: commissionData.length
    });
  }, [commissionData]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'credited':
        return <Badge bg="success">Credited</Badge>;
      case 'pending':
        return <Badge bg="warning">Pending</Badge>;
      case 'failed':
        return <Badge bg="danger">Failed</Badge>;
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const handleExport = () => {
    // Create CSV content
    const headers = ['Date', 'Service', 'Transaction Amount', 'Commission Rate (%)', 'Commission Earned', 'Status', 'Transaction ID'];
    const csvContent = [
      headers.join(','),
      ...commissionData.map(row => [
        row.date,
        row.service,
        row.transactionAmount,
        row.commissionRate,
        row.commissionEarned,
        row.status,
        row.transactionId
      ].join(','))
    ].join('\n');

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commission_summary_${dateRange.startDate}_to_${dateRange.endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaChartLine className="me-2" />
          Commission Summary
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {/* Date Range Filter */}
        <Row className="mb-4">
          <Col md={3}>
            <Form.Group>
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </Form.Group>
          </Col>
          <Col md={3} className="d-flex align-items-end">
            <Button variant="primary" className="w-100">
              <FaCalendar className="me-2" />
              Apply Filter
            </Button>
          </Col>
          <Col md={3} className="d-flex align-items-end">
            <Button variant="success" onClick={handleExport} className="w-100">
              <FaDownload className="me-2" />
              Export CSV
            </Button>
          </Col>
        </Row>

        {/* Summary Cards */}
        <Row className="mb-4">
          <Col md={3}>
            <div className="card bg-primary text-white">
              <div className="card-body text-center">
                <h5>Total Earned</h5>
                <h3><FaRupeeSign />{summary.totalEarned.toFixed(2)}</h3>
              </div>
            </div>
          </Col>
          <Col md={3}>
            <div className="card bg-success text-white">
              <div className="card-body text-center">
                <h5>Total Credited</h5>
                <h3><FaRupeeSign />{summary.totalCredited.toFixed(2)}</h3>
              </div>
            </div>
          </Col>
          <Col md={3}>
            <div className="card bg-warning text-white">
              <div className="card-body text-center">
                <h5>Total Pending</h5>
                <h3><FaRupeeSign />{summary.totalPending.toFixed(2)}</h3>
              </div>
            </div>
          </Col>
          <Col md={3}>
            <div className="card bg-info text-white">
              <div className="card-body text-center">
                <h5>Transactions</h5>
                <h3>{summary.totalTransactions}</h3>
              </div>
            </div>
          </Col>
        </Row>

        {/* Commission Table */}
        <div className="table-responsive">
          <Table striped bordered hover>
            <thead className="table-dark">
              <tr>
                <th>Date</th>
                <th>Service</th>
                <th>Transaction Amount</th>
                <th>Commission Rate</th>
                <th>Commission Earned</th>
                <th>Status</th>
                <th>Transaction ID</th>
              </tr>
            </thead>
            <tbody>
              {commissionData.map((item) => (
                <tr key={item.id}>
                  <td>{new Date(item.date).toLocaleDateString()}</td>
                  <td>{item.service}</td>
                  <td><FaRupeeSign />{item.transactionAmount.toLocaleString()}</td>
                  <td>{item.commissionRate}%</td>
                  <td className="text-success fw-bold">
                    <FaRupeeSign />{item.commissionEarned.toFixed(2)}
                  </td>
                  <td>{getStatusBadge(item.status)}</td>
                  <td>
                    <code className="text-primary">{item.transactionId}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {commissionData.length === 0 && (
          <div className="text-center py-5">
            <h5 className="text-muted">No commission data found for the selected period</h5>
          </div>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CommissionSummaryModal;
